import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Cliente, ClassificacaoCliente, NovoCliente } from '@pgcustom/shared-types';

/**
 * Filtros aceitos pela listagem HTTP real
 * (`apps/api/src/modules/clientes/clientes.schema.ts`,
 * `listarClientesQuerySchema`) — nunca inclui `incluirExcluidos`, que a
 * rota não aceita (mesma observação já registrada em `dashboard.api.ts`
 * para o KPI de clientes).
 */
export interface FiltroListaClientes {
  busca?: string;
  classificacao?: ClassificacaoCliente;
  limit?: number;
  offset?: number;
}

/** Campos aceitos por `PATCH /api/v1/clientes/:id` (`atualizarClienteSchema`) — todos opcionais. */
export interface AtualizarClienteInput {
  nome?: string;
  email?: string | null;
  cpf?: string | null;
  telefone?: string | null;
  classificacao?: ClassificacaoCliente;
}

/**
 * Adapter do módulo Clientes no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/clientes/*`
 * (`apps/api/src/modules/clientes/clientes.routes.ts`). `criar`/
 * `atualizar`/`excluir` propagam `ApiError` para quem chama (mesmo
 * padrão de `AuthApi.login`) — quem decide como exibir a falha é a tela,
 * nunca este adapter. `listar` devolve `Resultado<T>` (mesmo padrão de
 * `DashboardApi`) porque alimenta diretamente estados de UI
 * (carregando/vazio/erro/sem-permissão), não um fluxo de submissão único.
 */
export class ClientesApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroListaClientes): Promise<Resultado<Cliente[]>> {
    try {
      const resposta = await this.api.get<{ clientes: Cliente[] }>('/clientes', { ...filtro });
      if (resposta.clientes.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.clientes };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar clientes',
      };
    }
  }

  /** `GET /clientes/:id` — usado, por exemplo, pelo detalhe de Pedido (Sprint 4) para resolver o nome do cliente. Propaga `ApiError` (404/403), mesmo padrão de `criar`/`atualizar`. */
  async buscarPorId(id: string): Promise<Cliente> {
    const resposta = await this.api.get<{ cliente: Cliente }>(`/clientes/${id}`);
    return resposta.cliente;
  }

  async criar(dado: NovoCliente): Promise<Cliente> {
    const resposta = await this.api.post<{ cliente: Cliente }>('/clientes', dado);
    return resposta.cliente;
  }

  async atualizar(id: string, dado: AtualizarClienteInput): Promise<Cliente> {
    const resposta = await this.api.patch<{ cliente: Cliente }>(`/clientes/${id}`, dado);
    return resposta.cliente;
  }

  async excluir(id: string): Promise<void> {
    await this.api.excluir(`/clientes/${id}`);
  }
}
